import { getToken, clearToken } from '../utils/authToken';
import { useState, useEffect } from 'react';
import { API_URL, API } from '../config';
import { createPortal } from 'react-dom';
import { Share2, Link2, Copy, Check, X, Eye, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


const ShareConversationModal = ({ isOpen, onClose, conversationId, conversationTitle }) => {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);

  const isSpanish = language === 'es';

  const txt = {
    es: {
      title: 'Compartir Conversación',
      subtitle: 'Genera un enlace público para compartir esta conversación',
      createLink: 'Crear Enlace Público',
      shareLink: 'Enlace para Compartir',
      copyLink: 'Copiar Enlace',
      copied: '¡Copiado!',
      openInNewTab: 'Abrir en nueva pestaña',
      views: 'visualizaciones',
      createdAt: 'Creado',
      revokeLink: 'Revocar Enlace',
      revokeConfirm: '¿Revocar el enlace público? Ya no será accesible.',
      revoked: 'Enlace revocado',
      noConversation: 'Selecciona una conversación para compartir',
      errorCreate: 'Error al crear enlace',
      errorRevoke: 'Error al revocar enlace',
      linkCreated: '¡Enlace creado!',
      warning: 'Cualquier persona con este enlace podrá ver la conversación'
    },
    en: {
      title: 'Share Conversation',
      subtitle: 'Generate a public link to share this conversation',
      createLink: 'Create Public Link',
      shareLink: 'Share Link',
      copyLink: 'Copy Link',
      copied: 'Copied!',
      openInNewTab: 'Open in new tab',
      views: 'views',
      createdAt: 'Created',
      revokeLink: 'Revoke Link',
      revokeConfirm: 'Revoke the public link? It will no longer be accessible.',
      revoked: 'Link revoked',
      noConversation: 'Select a conversation to share',
      errorCreate: 'Error creating link',
      errorRevoke: 'Error revoking link',
      linkCreated: 'Link created!',
      warning: 'Anyone with this link will be able to view the conversation'
    }
  };

  const t = txt[language] || txt.es;

  useEffect(() => {
    if (isOpen && conversationId) {
      checkShareStatus();
    }
  }, [isOpen, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getHeaders = () => {
    const token = getToken();
    return {};  // P2.1 cookie-only via axios.defaults
  };

  const checkShareStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/share/conversation/${conversationId}/status`,
        { headers: getHeaders() }
      );
      if (response.data.is_shared) {
        setShareData(response.data);
      } else {
        setShareData(null);
      }
    } catch (error) {
      console.error('Error checking share status:', error);
    }
  };

  const createShareLink = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/share/conversation/${conversationId}`,
        {},
        { headers: getHeaders() }
      );
      setShareData({
        is_shared: true,
        share_code: response.data.share_code,
        share_url: response.data.share_url,
        views: 0
      });
      toast.success(t.linkCreated);
    } catch (error) {
      console.error('Error creating share link:', error);
      toast.error(t.errorCreate);
    } finally {
      setIsLoading(false);
    }
  };

  const revokeShareLink = async () => {
    if (!window.confirm(t.revokeConfirm)) return;
    
    setIsLoading(true);
    try {
      await axios.delete(
        `${API}/share/conversation/${conversationId}`,
        { headers: getHeaders() }
      );
      setShareData(null);
      toast.success(t.revoked);
    } catch (error) {
      console.error('Error revoking share link:', error);
      toast.error(t.errorRevoke);
    } finally {
      setIsLoading(false);
    }
  };

  const getFullShareUrl = () => {
    if (!shareData?.share_url) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}${shareData.share_url}`;
  };

  const copyToClipboard = async () => {
    const url = getFullShareUrl();
    
    // Helper function for textarea fallback
    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    // Try modern clipboard API first
    let success = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url);
        success = true;
      }
    } catch (clipErr) {
    }

    // Fallback to execCommand
    if (!success) {
      success = copyWithTextarea();
    }

    if (success) {
      setCopied(true);
      toast.success(t.copied);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">{t.title}</h2>
              <p className="text-xs text-zinc-400 truncate max-w-[200px]">
                {conversationTitle || 'Sin título'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {!conversationId ? (
            <div className="text-center py-8 text-zinc-500">
              <Share2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t.noConversation}</p>
            </div>
          ) : shareData ? (
            <>
              {/* Share Link Display */}
              <div className="space-y-3">
                <label className="block text-sm text-zinc-300 font-medium">
                  {t.shareLink}
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-sm text-zinc-300 truncate">
                    {getFullShareUrl()}
                  </div>
                  <button
                    onClick={copyToClipboard}
                    className={`p-2.5 rounded-lg transition-colors ${
                      copied 
                        ? 'bg-green-600 text-white' 
                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                    }`}
                    title={t.copyLink}
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                  <a
                    href={getFullShareUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg"
                    title={t.openInNewTab}
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-zinc-400">
                <div className="flex items-center gap-1.5">
                  <Eye className="w-4 h-4" />
                  <span>{shareData.views || 0} {t.views}</span>
                </div>
                {shareData.created_at && (
                  <span>
                    {t.createdAt}: {new Date(shareData.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}
                  </span>
                )}
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-400">⚠️ {t.warning}</p>
              </div>

              {/* Revoke Button */}
              <button
                onClick={revokeShareLink}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/30 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                {t.revokeLink}
              </button>
            </>
          ) : (
            <>
              <p className="text-zinc-400 text-sm">{t.subtitle}</p>
              
              {/* Create Link Button */}
              <button
                onClick={createShareLink}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Link2 className="w-5 h-5" />
                )}
                {t.createLink}
              </button>

              {/* Info */}
              <div className="p-3 bg-zinc-800 rounded-lg">
                <p className="text-sm text-zinc-400">⚠️ {t.warning}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareConversationModal;
